import axios from 'axios';

import { useCallback, useEffect, useRef, useState } from 'react';

// Redux.
import { connect } from 'react-redux';
import {
    setPodcastSubscribeStatus,
    saveShowSubscribeStatus,
    waitForShowPreferenceSaves,
    waitForSpeakerPreferenceSaves,
    setSpeakerSubscribeStatus,
    setTopicPreferenceStatus,
    syncSubscribeStatuses,
    syncTopicPreferences,
} from '../redux/actions/userActions';

// Router.
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

// Firebase.
import { getAnalytics, logEvent } from 'firebase/analytics';

import { Add } from '@mui/icons-material';

import { validTokenExists } from '../firebaseHelper';
import BottomMenu from '../components/BottomMenu';
import Loading from '../components/Loading';
import SearchTopicsDrawer from '../components/SearchTopicsDrawer';
import SearchShowsDrawer from '../components/SearchShowsDrawer';
import SearchSpeakersDrawer from '../components/SearchSpeakersDrawer';
import useShowPreferenceMutations from '../components/useShowPreferenceMutations';
import { saveShowStatus, showMutationFor, showPreferenceStatus } from '../utils/showSearch';
import {
    TopicPreferenceRow,
    SpeakerPreferenceRow,
    ShowPreferenceRow,
    analyticsEventName,
} from '../components/SettingsPreferenceComponents';
import { getSubscribeStatus, normalizeSubscribeStatus } from '../utils/subscribeStatus';

const settingsErrorMessage = (err, fallback) => (
    err?.response?.data?.error || err?.response?.data?.general || fallback
);

const NOOP = () => {};

const postTopicPreferenceStatus = (topicId, topicStatus) => (
    validTokenExists()
        .then(() => axios.post('/set_topic_status', {
            topicId,
            topicStatus,
            deferFeedRefresh: true,
        }))
);

const topicIdsFromResponse = (topicStatuses = []) => (
    topicStatuses
        .map((topicStatus) => topicStatus.topicId)
        .filter(Boolean)
);

const suggestedTopicIdsFromResponse = (suggestedTopics = [], explicitTopicIds = []) => {
    const explicitTopicIdSet = new Set(explicitTopicIds);
    return suggestedTopics
        .map((topic) => topic.topicId)
        .filter((topicId) => topicId && !explicitTopicIdSet.has(topicId));
};

const statusByTopicIdFromResponse = (topicStatuses = []) => (
    topicStatuses.reduce((statusById, topicStatus) => {
        const normalizedStatus = normalizeSubscribeStatus(topicStatus.topicStatus);
        if (topicStatus.topicId && normalizedStatus !== null) {
            statusById[topicStatus.topicId] = normalizedStatus;
        }
        return statusById;
    }, {})
);

const nilTopicIdsFromResponse = (topicStatuses = []) => (
    new Set(topicStatuses
        .filter((topicStatus) => topicStatus.topicId && normalizeSubscribeStatus(topicStatus.topicStatus) === null)
        .map((topicStatus) => topicStatus.topicId))
);

const appendUniqueById = (currentItems, nextItems) => {
    const currentIds = new Set(currentItems.map((item) => item.id));
    const uniqueNextItems = nextItems.filter((item) => item.id && !currentIds.has(item.id));
    return [...currentItems, ...uniqueNextItems];
};

const mergePreferenceItems = (currentItems, nextItems) => {
    const nextById = new Map(nextItems.map((item) => [item.id, item]));
    return appendUniqueById(currentItems.map((item) => nextById.get(item.id) || item), nextItems);
};

const SettingsAuthLoading = () => (
    <div className='container bottomPadding settingsOverviewContainer'>
        <Loading />
    </div>
);

const SettingsSubpageFrame = ({
    title,
    children,
    errorMessage,
    onDismissError,
    headerAction,
    titleClassName = 'settingsPreferencePageTitle',
}) => (
    <div className='container settingsOverviewContainer hasBottomMenu'>
        <div className='settingsOverviewContent settingsPreferencePageContent'>
            <div className='settingsPreferencePageHeader'>
                <div className='settingsPreferencePageTitleRow'>
                    <h1 className={titleClassName}>{title}</h1>
                    {headerAction && (
                        <div className='settingsPreferencePageHeaderAction'>
                            {headerAction}
                        </div>
                    )}
                </div>
            </div>
            {children}
        </div>

        {errorMessage && (
            <div className='settingsErrorOverlay' role='alert'>
                <span>{errorMessage}</span>
                <button type='button' onClick={onDismissError}>Dismiss</button>
            </div>
        )}
        <BottomMenu />
    </div>
);

const SettingsPageSkeleton = () => (
    <div className='settingsOverviewSkeleton glow'>
        <div className='loadingSettingForMeTopic' />
        <div className='loadingSettingForMeTopic' />
        <div className='loadingSettingForMeTopic' />
        <div className='loadingSettingForMeTopic' />
    </div>
);

export const SettingsTopics = ({
    authenticated = null,
    uid = '',
    setTopicPreferenceStatus = postTopicPreferenceStatus,
    syncTopicPreferences = NOOP,
}) => {
    const navigate = useNavigate();
    const [loadingTopics, setLoadingTopics] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [topicIds, setTopicIds] = useState([]);
    const [suggestedTopicIds, setSuggestedTopicIds] = useState([]);
    const [topicStatusById, setTopicStatusById] = useState({});
    const [topicIdsWithNilStatus, setTopicIdsWithNilStatus] = useState(new Set());
    const [searchTopicsDrawerOpen, setSearchTopicsDrawerOpen] = useState(false);

    const applyTopicStatuses = useCallback((data = {}) => {
        const nextTopicIds = topicIdsFromResponse(data.topicStatuses || []);
        setTopicIds(nextTopicIds);
        setSuggestedTopicIds(suggestedTopicIdsFromResponse(data.suggestedTopics || [], nextTopicIds));
        setTopicStatusById(statusByTopicIdFromResponse(data.topicStatuses || []));
        setTopicIdsWithNilStatus(nilTopicIdsFromResponse(data.topicStatuses || []));
    }, []);

    const loadTopicStatuses = useCallback(() => {
        setLoadingTopics(true);
        setErrorMessage(null);

        validTokenExists()
            .then(() => axios.post('/user_topic_statuses', {}))
            .then((res) => {
                syncTopicPreferences(res.data?.topicStatuses || [], {
                    replace: true,
                    uid,
                });
                applyTopicStatuses(res.data || {});
                setLoadingTopics(false);
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(settingsErrorMessage(err, 'Unable to load topics.'));
                setLoadingTopics(false);
            });
    }, [applyTopicStatuses, syncTopicPreferences, uid]);

    useEffect(() => {
        logEvent(getAnalytics(), 'screen_view', {
            firebase_screen: 'settings_topics',
        });
    }, []);

    useEffect(() => {
        if (authenticated === true) {
            loadTopicStatuses();
        }
    }, [authenticated, loadTopicStatuses]);

    const topicStatusFor = (topicId) => {
        if (topicIdsWithNilStatus.has(topicId)) {
            return null;
        }
        if (Object.prototype.hasOwnProperty.call(topicStatusById, topicId)) {
            return topicStatusById[topicId];
        }
        return null;
    };

    const setTopicStatus = (topicId, topicStatus) => {
        const normalizedTopicStatus = normalizeSubscribeStatus(topicStatus);
        const previousHadStatus = Object.prototype.hasOwnProperty.call(topicStatusById, topicId);
        const previousStatus = topicStatusById[topicId];
        const previousHadNilStatus = topicIdsWithNilStatus.has(topicId);

        setErrorMessage(null);
        setTopicStatusById((currentStatusById) => {
            const nextStatusById = { ...currentStatusById };
            if (normalizedTopicStatus === null) {
                delete nextStatusById[topicId];
            } else {
                nextStatusById[topicId] = normalizedTopicStatus;
            }
            return nextStatusById;
        });
        setTopicIdsWithNilStatus((currentTopicIdsWithNilStatus) => {
            const nextTopicIdsWithNilStatus = new Set(currentTopicIdsWithNilStatus);
            if (normalizedTopicStatus === null) {
                nextTopicIdsWithNilStatus.add(topicId);
            } else {
                nextTopicIdsWithNilStatus.delete(topicId);
            }
            return nextTopicIdsWithNilStatus;
        });

        return setTopicPreferenceStatus(topicId, normalizedTopicStatus)
            .catch((err) => {
                setTopicStatusById((current) => {
                    const next = { ...current };
                    if (previousHadStatus) next[topicId] = previousStatus;
                    else delete next[topicId];
                    return next;
                });
                setTopicIdsWithNilStatus((current) => {
                    const next = new Set(current);
                    if (previousHadNilStatus) next.add(topicId);
                    else next.delete(topicId);
                    return next;
                });
                setErrorMessage(settingsErrorMessage(err, 'Unable to update topic.'));
            });
    };

    const addTopicId = (topicId) => {
        const nextTopicId = typeof topicId === 'string' ? topicId.trim() : topicId;
        if (!nextTopicId) {
            return;
        }

        const hadTopicId = topicIds.includes(nextTopicId);
        const hadSuggestedTopicId = suggestedTopicIds.includes(nextTopicId);
        const hadTopicStatus = Object.prototype.hasOwnProperty.call(topicStatusById, nextTopicId);
        const previousTopicStatus = topicStatusById[nextTopicId];
        const hadNilStatus = topicIdsWithNilStatus.has(nextTopicId);

        setErrorMessage(null);
        setTopicIds((currentTopicIds) => (
            currentTopicIds.includes(nextTopicId) ? currentTopicIds : [nextTopicId, ...currentTopicIds]
        ));
        setSuggestedTopicIds((currentSuggestedTopicIds) => (
            currentSuggestedTopicIds.filter((currentTopicId) => currentTopicId !== nextTopicId)
        ));
        setTopicStatusById((currentStatusById) => ({
            ...currentStatusById,
            [nextTopicId]: true,
        }));
        setTopicIdsWithNilStatus((currentTopicIdsWithNilStatus) => {
            const nextTopicIdsWithNilStatus = new Set(currentTopicIdsWithNilStatus);
            nextTopicIdsWithNilStatus.delete(nextTopicId);
            return nextTopicIdsWithNilStatus;
        });

        setTopicPreferenceStatus(nextTopicId, true)
            .catch((err) => {
                if (!hadTopicId) {
                    setTopicIds((currentTopicIds) => (
                        currentTopicIds.filter((currentTopicId) => currentTopicId !== nextTopicId)
                    ));
                }
                setSuggestedTopicIds((currentSuggestedTopicIds) => {
                    const nextSuggestedTopicIds = currentSuggestedTopicIds.filter(
                        (currentTopicId) => currentTopicId !== nextTopicId,
                    );
                    return hadSuggestedTopicId ? [nextTopicId, ...nextSuggestedTopicIds] : nextSuggestedTopicIds;
                });
                setTopicStatusById((currentStatusById) => {
                    const nextStatusById = { ...currentStatusById };
                    if (hadTopicStatus) {
                        nextStatusById[nextTopicId] = previousTopicStatus;
                    } else {
                        delete nextStatusById[nextTopicId];
                    }
                    return nextStatusById;
                });
                setTopicIdsWithNilStatus((currentTopicIdsWithNilStatus) => {
                    const nextTopicIdsWithNilStatus = new Set(currentTopicIdsWithNilStatus);
                    if (hadNilStatus) {
                        nextTopicIdsWithNilStatus.add(nextTopicId);
                    } else {
                        nextTopicIdsWithNilStatus.delete(nextTopicId);
                    }
                    return nextTopicIdsWithNilStatus;
                });
                setErrorMessage(settingsErrorMessage(err, 'Unable to add topic.'));
            });
    };

    if (authenticated === false) {
        return <Navigate to={'/signin?redirect=%2Fsettings%2Ftopics'}></Navigate>;
    }

    if (authenticated !== true) {
        return <SettingsAuthLoading />;
    }

    const hasAnyTopics = topicIds.length > 0 || suggestedTopicIds.length > 0;

    return (
        <>
            <SettingsSubpageFrame
                title='Topics'
                errorMessage={errorMessage}
                onDismissError={() => setErrorMessage(null)}
                headerAction={(
                    <button
                        type='button'
                        className='settingsPreferencePageIconButton'
                        aria-label='Add topic'
                        onClick={() => setSearchTopicsDrawerOpen(true)}
                    >
                        <Add className='settingsPreferencePageIconButtonIcon' />
                    </button>
                )}
            >
                {loadingTopics ? (
                    <SettingsPageSkeleton />
                ) : (
                    <>
                        {!hasAnyTopics && (
                            <div className='settingsPreferencePageEmpty'>Nothing yet.</div>
                        )}

                        {topicIds.length > 0 && (
                            <div className='settingsPreferenceRows'>
                                {topicIds.map((topicId) => (
                                    <TopicPreferenceRow
                                        key={topicId}
                                        topicId={topicId}
                                        topicStatus={topicStatusFor(topicId)}
                                        setTopicStatus={setTopicStatus}
                                        onOpen={(selectedTopicId) => navigate(`/search/${encodeURIComponent(selectedTopicId)}`)}
                                    />
                                ))}
                            </div>
                        )}

                        {suggestedTopicIds.length > 0 && (
                            <div className='settingsPreferencePageSection'>
                                <h2 className='settingsPreferencePageSectionTitle'>Suggestions</h2>
                                <div className='settingsPreferenceRows'>
                                    {suggestedTopicIds.map((topicId) => (
                                        <TopicPreferenceRow
                                            key={topicId}
                                            topicId={topicId}
                                            topicStatus={topicStatusFor(topicId)}
                                            setTopicStatus={setTopicStatus}
                                            onOpen={(selectedTopicId) => navigate(`/search/${encodeURIComponent(selectedTopicId)}`)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </SettingsSubpageFrame>
            <SearchTopicsDrawer
                open={searchTopicsDrawerOpen}
                onClose={() => setSearchTopicsDrawerOpen(false)}
                addTopicId={addTopicId}
            />
        </>
    );
};

const PreferenceRowsPage = ({
    authenticated = null,
    uid = '',
    title,
    redirectPath,
    screenName,
    endpoint,
    responseItemsKey,
    statusPayloadKey,
    itemType,
    emptyTitle,
    emptyMessage,
    syncSubscribeStatuses,
    subscribeIds = [],
    subscribeStatusById = {},
    setSubscribeStatus,
    saveShowSubscribeStatus = saveShowStatus,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchStateKey = itemType === 'show' ? 'addShowsSearch' : 'addSpeakersSearch';
    const initialSearchRef = useRef(
        location.state?.[searchStateKey]?.uid === uid ? location.state[searchStateKey] : null,
    );
    const [searchOpen, setSearchOpen] = useState(Boolean(initialSearchRef.current));
    // Finish writes from the detail page we just left before reading statuses.
    // This snapshot excludes saves begun later in this visit; those are guarded
    // by the local status updates without blocking unrelated search rows.
    const [earlierSaves] = useState(() => itemType === 'show'
        ? waitForShowPreferenceSaves(uid) : waitForSpeakerPreferenceSaves(uid));
    const [earlierSavesFinished, setEarlierSavesFinished] = useState(false);
    const [items, setItems] = useState([]);
    const [initialLoading, setInitialLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [canLoadMore, setCanLoadMore] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const lastUpdatedRef = useRef(null);
    const loadingRef = useRef(false);
    const loadMoreObserverRef = useRef(null);
    const activeRef = useRef(true);
    const showUpdatesRef = useRef({});
    const [speakerUpdates, setSpeakerUpdates] = useState({});
    const speakerUpdatesRef = useRef({});
    const pendingSpeakersRef = useRef(new Map());
    const navigationRevisionRef = useRef(0);

    useEffect(() => {
        activeRef.current = true;
        earlierSaves.then(() => {
            if (activeRef.current) setEarlierSavesFinished(true);
        });
        return () => { activeRef.current = false; };
    }, [earlierSaves]);

    const showSaved = useCallback((result) => {
        showUpdatesRef.current[result.subscribeId] = result;
        // Keep a now-Undecided card for this visit so the user can undo it.
        setItems((current) => {
            if (current.some((item) => item.id === result.source.id)) {
                return current.map((item) => item.id === result.source.id ? result.source : item);
            }
            return result.subscribeStatus === null ? current : [result.source, ...current];
        });
        logEvent(getAnalytics(), analyticsEventName(result.subscribeStatus));
    }, []);
    const { save: saveShow, mutations: showMutations, savedShows, waitForSave } =
        useShowPreferenceMutations(saveShowSubscribeStatus, showSaved);
    const showMutationsRef = useRef(showMutations);
    showMutationsRef.current = showMutations;

    const speakerStatusFor = (speaker) => (
        Object.prototype.hasOwnProperty.call(speakerUpdates, speaker.id)
            ? speakerUpdates[speaker.id] : getSubscribeStatus(subscribeStatusById, subscribeIds, speaker.id)
    );

    const saveSpeaker = (speaker, status, previousStatus) => {
        const speakerId = speaker.id;
        if (pendingSpeakersRef.current.has(speakerId)) return pendingSpeakersRef.current.get(speakerId);
        const updateStatus = (nextStatus) => {
            speakerUpdatesRef.current = { ...speakerUpdatesRef.current, [speakerId]: nextStatus };
            setSpeakerUpdates(speakerUpdatesRef.current);
        };
        updateStatus(status);
        setErrorMessage(null);
        const request = Promise.resolve().then(() => (
            setSubscribeStatus(speakerId, status, { suppressErrorAlert: true })
        )).then((result) => {
            if (!activeRef.current) return;
            if (!result) throw new Error('Unable to save speaker. Please try again.');
            // Keep the selected speaker's metadata even if search was cleared or closed.
            setItems((current) => {
                if (current.some((item) => item.id === speakerId)) {
                    return current.map((item) => item.id === speakerId ? speaker : item);
                }
                return status === null ? current : [speaker, ...current];
            });
            logEvent(getAnalytics(), analyticsEventName(status));
        }).catch((error) => {
            if (!activeRef.current) return;
            updateStatus(previousStatus);
            setErrorMessage(settingsErrorMessage(error, 'Unable to save speaker. Please try again.'));
        }).finally(() => {
            pendingSpeakersRef.current.delete(speakerId);
        });
        pendingSpeakersRef.current.set(speakerId, request);
        return request;
    };

    const syncShowResults = useCallback((shows) => {
        syncSubscribeStatuses({
            podcastSubscribeStatuses: shows.filter((show) => show.source?.id &&
                !showUpdatesRef.current[show.source.id] && !showMutationFor(showMutationsRef.current, show))
                .map((show) => ({ subscribeId: show.source.id, subscribeStatus: show.subscribeStatus })),
        });
    }, [syncSubscribeStatuses]);

    const closeSearch = () => {
        navigationRevisionRef.current += 1;
        setSearchOpen(false);
        initialSearchRef.current = null;
        if (location.state?.[searchStateKey]) {
            navigate(`${location.pathname}${location.search}`, {
                replace: true, state: { ...location.state, [searchStateKey]: null },
            });
        }
    };

    const openSpeaker = async (speaker, searchState) => {
        const navigationRevision = ++navigationRevisionRef.current;
        await earlierSaves;
        await pendingSpeakersRef.current.get(speaker.id);
        if (!activeRef.current || navigationRevision !== navigationRevisionRef.current) return;
        if (searchState) {
            navigate(`${location.pathname}${location.search}`, {
                replace: true, state: { ...location.state, addSpeakersSearch: { ...searchState, uid } },
            });
        }
        navigate(`/speaker/${encodeURIComponent(speaker.id)}`);
    };

    const openSearchShow = async (show, searchState) => {
        const navigationRevision = ++navigationRevisionRef.current;
        const saved = await waitForSave(show);
        if (!activeRef.current || navigationRevision !== navigationRevisionRef.current) return;
        const source = saved?.source || show.source;
        // Store only this history entry's search state, scoped to the signed-in user.
        navigate(`${location.pathname}${location.search}`, {
            replace: true, state: { ...location.state, addShowsSearch: { ...searchState, uid } },
        });
        if (source?.id) {
            navigate(`/podcast/${source.id}`);
        } else {
            navigate('/load_podcast', {
                state: { title: show.name, imageSrc: show.imageURL, rssURL: show.rssURL, appleCollectionId: show.appleCollectionId },
            });
        }
    };

    const loadPreferences = useCallback((reset = false) => {
        if (authenticated !== true || loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        const requestBody = {};
        if (!reset && lastUpdatedRef.current) {
            requestBody.lastUpdated = lastUpdatedRef.current;
        }
        if (reset) {
            lastUpdatedRef.current = null;
            setCanLoadMore(true);
            setInitialLoading(true);
        } else {
            setLoadingMore(true);
        }
        setLoadError(null);

        earlierSaves.then(() => validTokenExists())
            .then(() => axios.post(endpoint, requestBody))
            .then((res) => {
                if (!activeRef.current) return;
                const data = res.data || {};
                const nextItems = data[responseItemsKey] || [];
                syncSubscribeStatuses({
                    [statusPayloadKey]: (data.subscribeStatuses || []).filter((entry) => (
                        itemType === 'show'
                            ? !showUpdatesRef.current[entry.subscribeId] && !showMutationsRef.current[`source:${entry.subscribeId}`]
                            : !Object.prototype.hasOwnProperty.call(speakerUpdatesRef.current, entry.subscribeId)
                    )),
                });
                setItems((currentItems) => {
                    // Keep this visit's rows, including cleared preferences, in
                    // place when a refresh overlaps pending or completed edits.
                    const hasLocalEdits = itemType === 'show'
                        ? Object.keys(showUpdatesRef.current).length > 0 || Object.values(showMutationsRef.current).some(Boolean)
                        : Object.keys(speakerUpdatesRef.current).length > 0;
                    const mergedItems = reset && !hasLocalEdits ? nextItems : mergePreferenceItems(currentItems, nextItems);
                    return itemType === 'show'
                        ? mergedItems.map((item) => showUpdatesRef.current[item.id]?.source || item)
                        : mergedItems;
                });
                lastUpdatedRef.current = data.lastUpdated || null;
                // A page can scan subscriptions without finding their metadata.
                // Only an exhausted or non-advancing cursor ends pagination.
                setCanLoadMore(Boolean(data.lastUpdated) && data.lastUpdated !== requestBody.lastUpdated);
                setInitialLoading(false);
                setLoadingMore(false);
                loadingRef.current = false;
            })
            .catch((err) => {
                if (!activeRef.current) return;
                console.log(err);
                setLoadError(settingsErrorMessage(err, `Unable to load ${title.toLowerCase()}.`));
                setInitialLoading(false);
                setLoadingMore(false);
                loadingRef.current = false;
            });
    }, [authenticated, endpoint, responseItemsKey, statusPayloadKey, syncSubscribeStatuses, title, itemType, earlierSaves]);

    useEffect(() => {
        logEvent(getAnalytics(), 'screen_view', {
            firebase_screen: screenName,
        });
    }, [screenName]);

    useEffect(() => {
        if (authenticated === true) {
            loadPreferences(true);
        }
    }, [authenticated, loadPreferences]);

    useEffect(() => {
        if (
            !loadMoreObserverRef.current ||
            initialLoading ||
            loadingMore ||
            !canLoadMore ||
            loadError ||
            searchOpen
        ) {
            return undefined;
        }

        const targetNode = loadMoreObserverRef.current;
        let observing = true;
        const observer = new IntersectionObserver(
            (entries) => {
                if (observing && entries[0].isIntersecting) {
                    loadPreferences(false);
                }
            },
            {
                root: null,
                rootMargin: '120px 0px',
            },
        );

        observer.observe(targetNode);

        return () => {
            observing = false;
            observer.unobserve(targetNode);
        };
    }, [canLoadMore, initialLoading, items, loadError, loadPreferences, loadingMore, searchOpen]);

    if (authenticated === false) {
        return <Navigate to={`/signin?redirect=${encodeURIComponent(redirectPath)}`}></Navigate>;
    }

    if (authenticated !== true) {
        return <SettingsAuthLoading />;
    }

    return (
        <>
        <SettingsSubpageFrame
            title={title}
            titleClassName='largeHeading'
            errorMessage={itemType === 'speaker' && searchOpen ? null : errorMessage}
            onDismissError={() => setErrorMessage(null)}
            headerAction={(
                <button type='button' className='settingsPreferencePageIconButton'
                    aria-label={itemType === 'show' ? 'Add shows' : 'Add speakers'}
                    onClick={() => setSearchOpen(true)}>
                    <Add className='settingsPreferencePageIconButtonIcon' />
                </button>
            )}
        >
            {initialLoading ? (
                <SettingsPageSkeleton />
            ) : (
                <>
                    {items.length === 0 && !canLoadMore ? (
                        <div className='settingsPreferencePageEmpty'>
                            <h2 className='settingsPreferenceEmptyTitle'>{emptyTitle}</h2>
                            <p>{emptyMessage}</p>
                        </div>
                    ) : (
                        <div className='settingsPreferenceRows'>
                            {items.map((item) => {
                                const showUpdate = savedShows[`source:${item.id}`];
                                const showMutation = showMutations[`source:${item.id}`];
                                const storedStatus = getSubscribeStatus(
                                    subscribeStatusById,
                                    subscribeIds,
                                    item.id,
                                );
                                const currentStatus = itemType === 'show'
                                    ? showPreferenceStatus(showUpdate ? showUpdate.subscribeStatus : storedStatus, showMutation)
                                    : speakerStatusFor(item);
                                const onStatusChange = (nextStatus) => {
                                    if (itemType === 'show') {
                                        saveShow({ source: item, subscribeStatus: currentStatus }, nextStatus);
                                        return;
                                    }
                                    saveSpeaker(item, nextStatus, currentStatus);
                                };

                                if (itemType === 'speaker') {
                                    return (
                                        <SpeakerPreferenceRow
                                            key={item.id}
                                            speaker={item}
                                            status={currentStatus}
                                            onOpen={() => openSpeaker(item)}
                                            onStatusChange={onStatusChange}
                                        />
                                    );
                                }

                                return (
                                    <ShowPreferenceRow
                                        key={item.id}
                                        source={item}
                                        status={currentStatus}
                                        onOpen={async () => {
                                            await waitForSave({ source: item });
                                            if (activeRef.current) navigate(`/podcast/${item.id}`);
                                        }}
                                        onStatusChange={onStatusChange}
                                        mutation={showMutation}
                                        onRetry={() => saveShow({ source: item, subscribeStatus: currentStatus }, showMutation.status)}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {canLoadMore && (
                        <div
                            ref={loadMoreObserverRef}
                            className='settingsLoadMoreRow settingsLoadMoreSentinel'
                            aria-live='polite'
                        >
                            {loadingMore && (
                                <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
                            )}
                            {loadError && (
                                <div role='alert'>
                                    <p>{loadError}</p>
                                    <button type='button' onClick={() => loadPreferences(!lastUpdatedRef.current)}>
                                        Retry
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </SettingsSubpageFrame>
        {itemType === 'show' && (
            <SearchShowsDrawer open={searchOpen} onClose={closeSearch}
                initialState={initialSearchRef.current}
                ready={earlierSaves}
                onOpenShow={openSearchShow} onSaveShow={saveShow} onResults={syncShowResults}
                mutations={showMutations} savedShows={savedShows} />
        )}
        {itemType === 'speaker' && (
            <SearchSpeakersDrawer open={searchOpen} onClose={closeSearch}
                initialState={initialSearchRef.current}
                preferencesReady={earlierSavesFinished}
                onOpenSpeaker={openSpeaker} statusFor={speakerStatusFor}
                onSaveSpeaker={(speaker, status) => saveSpeaker(speaker, status, speakerStatusFor(speaker))}
                saveError={errorMessage} onDismissSaveError={() => setErrorMessage(null)} />
        )}
        </>
    );
};

export const SettingsSpeakers = ({
    authenticated = null,
    uid = '',
    syncSubscribeStatuses,
    setSpeakerSubscribeStatus,
    speakerSubscribeIds = [],
    speakerSubscribeStatusById = {},
}) => (
    <PreferenceRowsPage
        key={`${uid}:${authenticated}`}
        uid={uid}
        authenticated={authenticated}
        title='Speakers'
        redirectPath='/settings/speakers'
        screenName='settings_speakers'
        endpoint='/subscribed_speaker_statuses'
        responseItemsKey='speakers'
        statusPayloadKey='speakerSubscribeStatuses'
        itemType='speaker'
        emptyTitle='Choose who you want to hear'
        emptyMessage='Tap + to find speakers and tell us which ones you like.'
        syncSubscribeStatuses={syncSubscribeStatuses}
        subscribeIds={speakerSubscribeIds}
        subscribeStatusById={speakerSubscribeStatusById}
        setSubscribeStatus={setSpeakerSubscribeStatus}
    />
);

export const SettingsShows = ({
    authenticated = null,
    uid = '',
    syncSubscribeStatuses,
    setPodcastSubscribeStatus,
    podcastSubscribeIds = [],
    podcastSubscribeStatusById = {},
    saveShowSubscribeStatus,
}) => (
    <PreferenceRowsPage
        key={`${uid}:${authenticated}`}
        uid={uid}
        authenticated={authenticated}
        title='Shows'
        redirectPath='/settings/shows'
        screenName='settings_shows'
        endpoint='/subscribed_show_statuses'
        responseItemsKey='sources'
        statusPayloadKey='podcastSubscribeStatuses'
        itemType='show'
        emptyTitle='Find your next favorite'
        emptyMessage='Tap + to find shows and tell us which ones you like.'
        syncSubscribeStatuses={syncSubscribeStatuses}
        subscribeIds={podcastSubscribeIds}
        subscribeStatusById={podcastSubscribeStatusById}
        setSubscribeStatus={setPodcastSubscribeStatus}
        saveShowSubscribeStatus={saveShowSubscribeStatus}
    />
);

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
    speakerSubscribeIds: state.user.speakerSubscribeIds,
    speakerSubscribeStatusById: state.user.speakerSubscribeStatusById,
    podcastSubscribeIds: state.user.podcastSubscribeIds,
    podcastSubscribeStatusById: state.user.podcastSubscribeStatusById,
});

const mapActionsToProps = {
    syncSubscribeStatuses,
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    saveShowSubscribeStatus,
    setTopicPreferenceStatus,
    syncTopicPreferences,
};

export const ConnectedSettingsTopics = connect(mapStateToProps, mapActionsToProps)(SettingsTopics);
export const ConnectedSettingsSpeakers = connect(mapStateToProps, mapActionsToProps)(SettingsSpeakers);
export const ConnectedSettingsShows = connect(mapStateToProps, mapActionsToProps)(SettingsShows);
